// @flow
import { createSelector } from 'reselect';
import createCachedSelector, { LruObjectCache } from 're-reselect';

import { isErrorType } from 'data/institutionLogins/utils';

// import { getLogger } from 'utils/logger';

import { REDUCER_KEY } from './reducer';
import type { InstitutionLoginsStore } from './types';

// const log = getLogger('institutionLogins/selectors');

export const getInstitutionLoginsStore = (state: any): InstitutionLoginsStore => state[REDUCER_KEY];

export const getInstitutionLoginsById = (state: any) => getInstitutionLoginsStore(state).resourcesById;
export const getLoadPending = (state: any) => getInstitutionLoginsStore(state).loadPending;
export const getLastSyncDate = (state: any) => getInstitutionLoginsStore(state).lastSyncDate;

export const getInstitutionLoginForId = (state: any, id: string) => getInstitutionLoginsById(state).get(id);

export const getIsRefreshingAll = (state: any) => getInstitutionLoginsStore(state).isRefreshingAll;
export const getIdsRefreshing = (state: any) => getInstitutionLoginsStore(state).idsRefreshing;

export const getisRefreshingSome = (state: any) => getIdsRefreshing(state).size > 0;
export const getIsRefreshing = (state: any) => getIsRefreshingAll(state) || getIdsRefreshing(state).size > 0;

export const getIdsRefreshingWithUserActionsPending = (state: any) =>
  getInstitutionLoginsStore(state).idsRefreshingWithUserActionsPending;

export const getIdsRefreshingWithCancelledUserActions = (state: any) =>
  getInstitutionLoginsStore(state).idsRefreshingWithCancelledUserActions;

export const getCredentialBlobsForRefreshingLogins = (state: any) =>
  getInstitutionLoginsStore(state).credentialBlobsForRefreshingLogins;

export const getCredentialsForRefreshingLogins = (state: any) =>
  getInstitutionLoginsStore(state).credentialsForRefreshingLogins;

export const hasUserActionBeenQueued = (state: any, institutionLoginId: string) => (
  getIdsRefreshingWithUserActionsPending(state).has(institutionLoginId) ||
  getIdsRefreshingWithCancelledUserActions(state).has(institutionLoginId)
);

export const isUserActionPending = createSelector(
  getIdsRefreshingWithUserActionsPending,
  (_, props) => props ? props.institutionLoginIdId : null,
  (idsRefreshingWithUserActionsPending, institutionLoginId) => (
    institutionLoginId == null ?
      idsRefreshingWithUserActionsPending.size > 0 :
      idsRefreshingWithUserActionsPending.has(institutionLoginId)
  )
);
export const wasUserActionCancelled = (state: any, institutionLoginId: string) =>
  getIdsRefreshingWithCancelledUserActions(state).has(institutionLoginId);

export const getInstitutionLoginsByName = createSelector(
  getInstitutionLoginsById,
  (institutionLoginsById) => !institutionLoginsById ? null :
    institutionLoginsById.sort((a, b) => (
      a.name.localeCompare(
        b.name,
        undefined,
        { numeric: true, sensitivity: 'base' }
      )
    ))
);

export const isInstitutionLoginAmbiguous = createSelector(
  getInstitutionLoginsById,
  (_, props) => props.institutionLoginId,
  (institutionLoginsById, institutionLoginId) => {
    if (!institutionLoginId) {
      return undefined;
    }
    const institutionLogin = institutionLoginsById.get(institutionLoginId);
    const ambiguousInstitutionLogin = institutionLoginsById.find((iInstitutionLogin) =>
      iInstitutionLogin.institutionId === institutionLogin.institutionId && iInstitutionLogin.id !== institutionLogin.id);
    return ambiguousInstitutionLogin !== undefined;
  }
);

export const getErrorCount = createSelector(
  (state, accounts) => accounts,
  getInstitutionLoginsByName,
  (accounts, institutionLoginsByName) => {
    let count = 0;
    let institutionErrorCount = 0;
    let acctErrorCount = 0;
    let institutionLogin = {};
    institutionLoginsByName && institutionLoginsByName.forEach((institution) => {
      const errorStatus = institution.aggregators && institution.aggregators.get(0) && institution.aggregators.get(0).aggStatus;
      const account = accounts ? accounts.get(institution.id) : null;
      account && account.forEach((acc) => {
        const aggStatus = acc.aggregators && acc.aggregators.get(0) && acc.aggregators.get(0).aggStatus;
        if(aggStatus && isErrorType(aggStatus)) {
          institutionLogin = institution;
          acctErrorCount += 1;
        }
      });
      if ((!account || account.size === 0)) {
        return;
      }
      if (isErrorType(errorStatus)) {
        institutionLogin = institution;
        institutionErrorCount += 1;
      }
      count = institutionErrorCount + acctErrorCount;
    });
    return {count, institutionLogin};
  }
);

export const getIsConnected = createCachedSelector(
  getInstitutionLoginsById,
  (_, props) => props.institutionLoginId,
  (institutionLogins, id) => {
    if (!id || id === '0') {
      return false;
    }
    const institutionLogin = institutionLogins.get(id);
    if (!institutionLogin) {
      return false;
    }
    return institutionLogin.aggregators && institutionLogin.aggregators.size > 0;
  }
)({
  keySelector: (state, props) => props.institutionLoginId ? props.institutionLoginId : '0',
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
});

